/**
 * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
 * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
 *
 * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
 */
import { createRecordingEvidence } from './helpers.js';
import { sandboxLocalResponsePersistenceStrategy } from './sandboxUploadAdapter.js';

function canRestoreFromLocalSessionFile(localSessionFile) {
    return typeof Blob !== 'undefined' && localSessionFile instanceof Blob;
}

export function restoreVideoInteractionState({ interactionStateStore, localObjectUrls }) {
    const storedState = interactionStateStore.get().state || {};
    const storedValue = interactionStateStore.getResponseValue();
    let storedResponseValue = null;
    let evidence = storedState.evidence || null;
    let previewUrl = storedState.localPreviewUrl || '';
    const localSessionFile = storedState.localSessionFile || null;
    const responseNotice = storedState.responseNotice || '';

    if (storedValue) {
        storedResponseValue = storedValue;
        evidence = createRecordingEvidence({
            sizeBytes: storedValue.sizeBytes,
            durationMs: storedValue.durationMs,
            mimeType: storedValue.recorderMimeType || storedValue.mime,
            uploadDurationMs: storedValue.uploadDurationMs,
            persistenceStrategy: storedValue.persistenceStrategy || storedValue.uploadStrategy
        });
        previewUrl = storedValue.downloadUrl || storedValue.link || previewUrl;
    }

    if (previewUrl.startsWith('blob:')) {
        const shouldRestoreFromLocalSessionFile =
            storedValue?.persistenceStrategy === sandboxLocalResponsePersistenceStrategy || !!localSessionFile;

        if (shouldRestoreFromLocalSessionFile && canRestoreFromLocalSessionFile(localSessionFile)) {
            previewUrl = URL.createObjectURL(localSessionFile);
            localObjectUrls.add(previewUrl);
        } else {
            previewUrl = '';
        }
    }

    return {
        storedResponseValue,
        evidence,
        previewUrl,
        responseNotice,
        shouldInitializeEmptyState: !interactionStateStore.hasResponse() && !storedState.localPreviewUrl
    };
}

export function persistVideoInteractionState({
    interactionStateStore,
    qtiClass,
    typeIdentifier,
    cardinality,
    responseValue,
    evidence,
    responseNotice,
    previewUrl,
    localSessionFile = null,
    persistLocalPreview = false,
    localPreviewUrlOverride = '',
    validity = true
}) {
    interactionStateStore.merge({
        qtiClass,
        typeIdentifier,
        state: {
            evidence,
            localSessionFile,
            localPreviewUrl: persistLocalPreview ? localPreviewUrlOverride || previewUrl : '',
            responseNotice
        }
    });
    interactionStateStore.setResponseValue(
        {
            cardinality,
            baseType: responseValue ? 'fileHash' : 'file',
            value: responseValue
        },
        validity
    );
}
